// Pure ICS (RFC 5545) builder for the "+ Calendar" affordance on the event
// page. No DOM, no Convex — just strings in, an .ics document out — so it is
// unit-tested in isolation (tests/unit/ics.test.ts). The one function that
// touches the DOM (downloadIcs) is separated below and never runs at import.

export interface IcsEventInput {
  /** Stable UID seed — the event id. Combined with a domain to form the UID. */
  uid: string;
  /** Event title (SUMMARY). */
  title: string;
  /** ISO start; required. */
  start: string;
  /** ISO end; when absent DTEND falls back to start + 2h. */
  end?: string | null;
  /** LOCATION line; omitted when empty. */
  location?: string | null;
  /** DESCRIPTION line (e.g. the ticket link); omitted when empty. */
  description?: string | null;
  /** URL property; omitted when empty. */
  url?: string | null;
  /**
   * DTSTAMP override (ISO). Pure-function seam so tests get a deterministic
   * stamp; production omits it and the current time is used.
   */
  now?: string | null;
  /**
   * Emit DTSTART/DTEND as floating local times (no Z, RFC 5545 §3.3.5 form 1).
   * The schedule strings are timezone-naive ("2026-08-22T14:00" means 14:00 at
   * the venue); floating renders that wall-clock time on every device, where
   * UTC conversion would depend on whichever machine parsed the string.
   */
  floating?: boolean;
}

/**
 * Escape a value for an ICS text field: backslash, semicolon and comma are
 * escaped, and every newline flavour collapses to the literal `\n` sequence
 * (RFC 5545 §3.3.11). Applied to SUMMARY / LOCATION / DESCRIPTION.
 */
export function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r\n|\r|\n/g, '\\n');
}

/**
 * Format an ISO instant as a UTC ICS timestamp: `YYYYMMDDTHHMMSSZ`.
 * Returns null for anything that isn't a valid date.
 */
export function formatIcsUtc(iso: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
}

/**
 * Format a timezone-naive ISO string as a floating ICS timestamp:
 * `YYYYMMDDTHHMMSS` (no Z). Pure string slicing — no Date parsing, so the
 * result never depends on the machine's timezone. Null when the shape is off.
 */
export function formatIcsFloating(iso: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(iso);
  if (!m) return null;
  return `${m[1]}${m[2]}${m[3]}T${m[4]}${m[5]}${m[6] ?? '00'}`;
}

/** Floating end fallback: start + 2h, computed in a fixed frame (UTC) and
 *  formatted back naive so the arithmetic is timezone-free. */
function floatingEndFallback(start: string): string | null {
  const base = new Date(`${start.slice(0, 16)}:00Z`);
  if (Number.isNaN(base.getTime())) return null;
  const end = new Date(base.getTime() + FALLBACK_DURATION_MS);
  return formatIcsFloating(end.toISOString().slice(0, 19));
}

const FALLBACK_DURATION_MS = 2 * 60 * 60 * 1000;

/**
 * Build a VCALENDAR document (single VEVENT) from an event. Lines are joined
 * with CRLF per spec. Returns null when the start is unparseable (no valid
 * VEVENT is possible). DTEND falls back to start + 2h when end is missing or
 * invalid.
 */
export function buildEventIcs(input: IcsEventInput): string | null {
  const format = input.floating ? formatIcsFloating : formatIcsUtc;
  const dtStart = format(input.start);
  if (!dtStart) return null;

  let dtEnd: string | null = input.end ? format(input.end) : null;
  if (!dtEnd) {
    dtEnd = input.floating
      ? floatingEndFallback(input.start)
      : formatIcsUtc(new Date(new Date(input.start).getTime() + FALLBACK_DURATION_MS).toISOString());
  }
  if (!dtEnd) return null;

  const stampSource = input.now ?? new Date().toISOString();
  const dtStamp = formatIcsUtc(stampSource) ?? formatIcsUtc(new Date().toISOString())!;

  const uid = `${input.uid}@gravity`;

  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Gravity//Event//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${escapeIcsText(uid)}`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    `SUMMARY:${escapeIcsText(input.title)}`
  ];

  const location = input.location?.trim();
  if (location) lines.push(`LOCATION:${escapeIcsText(location)}`);

  const description = input.description?.trim();
  if (description) lines.push(`DESCRIPTION:${escapeIcsText(description)}`);

  const url = input.url?.trim();
  if (url) lines.push(`URL:${escapeIcsText(url)}`);

  lines.push('END:VEVENT', 'END:VCALENDAR');
  return lines.join('\r\n');
}

export interface GoogleCalendarInput {
  title: string;
  /** Timezone-naive ISO start, same shape the event schedule stores. */
  start: string;
  end?: string | null;
  location?: string | null;
  details?: string | null;
  /** IANA zone the naive times belong to (`ctz` pins them, e.g. Europe/Lisbon). */
  timezone?: string | null;
}

/**
 * Build a Google Calendar "add event" template URL — on Android (and any
 * device with Google Calendar) this opens a prefilled event with one-tap
 * Save, no .ics file involved. Dates go in floating form with `ctz` pinning
 * the venue's zone. Null when the start is unusable.
 */
export function buildGoogleCalendarUrl(input: GoogleCalendarInput): string | null {
  const start = formatIcsFloating(input.start);
  if (!start) return null;
  const end = (input.end ? formatIcsFloating(input.end) : null) ?? floatingEndFallback(input.start);
  if (!end) return null;

  const params = new URLSearchParams({ action: 'TEMPLATE', text: input.title, dates: `${start}/${end}` });
  if (input.timezone?.trim()) params.set('ctz', input.timezone.trim());
  if (input.location?.trim()) params.set('location', input.location.trim());
  if (input.details?.trim()) params.set('details', input.details.trim());
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Trigger a client-side download of an .ics document. Touches the DOM only
 * when called (never at import), so buildEventIcs stays pure and testable.
 * Returns true on success, false if the input produced no valid VEVENT.
 */
export function downloadIcs(input: IcsEventInput, filename?: string): boolean {
  const ics = buildEventIcs(input);
  if (!ics) return false;
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename ?? `${input.uid}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
  return true;
}
